import type { EstimatedCoverage } from "api/types";

/** Clave compuesta producto + índice de columna (`42|3`). */
export type PeriodDeviationKey = string;

/**
 * Borrador listo para persistir vía `simulation_deviation_note` (Agente 8).
 * `simulationResultProductId` se hidrata al cruzar con el payload JSON:API de resultados.
 */
export type DeviationNoteSelection = {
  deviationNoteId: string;
  label: string;
};

export type SimulationPeriodDeviationDraft = {
  productId: number;
  erpCode: string;
  periodKey: string;
  periodStartDate?: string;
  periodIndex: number;
  deviationNoteId: string | null;
  /** Nombre del catálogo; evita precargar `deviation-categories` al abrir sesión. */
  deviationNoteLabel?: string;
  comments?: string;
  simulationResultProductId?: string;
};

export type SimulationDeviationNotesState = {
  /** Motivo columna «Actual» por producto. */
  byProductGeneral: Record<number, DeviationNoteSelection>;
  /** Motivo de desviación por producto y período (columnas mensuales/semanales). */
  byProductPeriod: Record<PeriodDeviationKey, SimulationPeriodDeviationDraft>;
};

/** Entrada para POST `/simulations/{id}/deviations/{deviation_id}/result-products/{srp_id}/`. */
export type SimulationDeviationNoteCreateInput = {
  simulationId: string;
  deviationNoteId: string;
  productId: string;
  erpCode: string;
  periodKey: string;
  periodStartDate?: string;
  periodIndex: number;
  simulationResultProductId?: string;
  comments?: string;
  /** Columna «Actual» → `SimulationDeviationNote.is_actual`. */
  isActual?: boolean;
};

function coerceChronologyInt(value: unknown): number | null {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const n = Number(value);
  return Number.isFinite(n) ? Math.trunc(n) : null;
}

/** Identificador estable del período según cronología activa. */
export function buildPeriodKey(
  targetDate: EstimatedCoverage["targetDate"],
  chronologyParameter: string,
): string {
  const year = coerceChronologyInt(targetDate.year);
  const month = coerceChronologyInt(targetDate.month);
  const week = coerceChronologyInt(targetDate.week);

  if (chronologyParameter === "year_month") {
    if (year === null || month === null || month < 1 || month > 12) {
      return "unknown";
    }
    return `${year}-${String(month).padStart(2, "0")}`;
  }

  if (year === null || week === null || week < 1) {
    return "unknown";
  }
  return `${year}-W${String(week).padStart(2, "0")}`;
}

export function buildPeriodDeviationKey(
  productId: number,
  periodIndex: number,
): PeriodDeviationKey {
  return `${productId}|${periodIndex}`;
}

/** Fecha ISO `YYYY-MM-DD` del inicio de período (mes o aprox. semana). */
export function buildPeriodStartIso(
  targetDate: EstimatedCoverage["targetDate"],
  chronologyParameter: string,
): string | undefined {
  const year = coerceChronologyInt(targetDate.year);
  const month = coerceChronologyInt(targetDate.month);
  const week = coerceChronologyInt(targetDate.week);

  if (year === null) {
    return undefined;
  }

  if (chronologyParameter === "year_month") {
    if (month === null || month < 1 || month > 12) {
      return undefined;
    }
    return `${year}-${String(month).padStart(2, "0")}-01`;
  }

  if (week === null || week < 1) {
    return undefined;
  }

  const jan4 = new Date(Date.UTC(year, 0, 4));
  const dayOfWeek = (jan4.getUTCDay() + 6) % 7;
  const week1Monday = new Date(jan4);
  week1Monday.setUTCDate(jan4.getUTCDate() - dayOfWeek);
  const start = new Date(week1Monday);
  start.setUTCDate(week1Monday.getUTCDate() + (week - 1) * 7);
  const y = start.getUTCFullYear();
  const m = String(start.getUTCMonth() + 1).padStart(2, "0");
  const d = String(start.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function createEmptyDeviationNotesState(): SimulationDeviationNotesState {
  return {
    byProductGeneral: {},
    byProductPeriod: {},
  };
}

export function collectSimulationDeviationNotePayloads(
  state: SimulationDeviationNotesState,
  simulationId: string,
  erpCodeByProductId: Map<number, string>,
): SimulationDeviationNoteCreateInput[] {
  const periodRows = Object.values(state.byProductPeriod)
    .filter((row) => row.deviationNoteId != null && row.deviationNoteId !== "")
    .map((row) => ({
      simulationId,
      deviationNoteId: row.deviationNoteId as string,
      productId: String(row.productId),
      erpCode: row.erpCode,
      periodKey: row.periodKey,
      periodStartDate: row.periodStartDate,
      periodIndex: row.periodIndex,
      simulationResultProductId: row.simulationResultProductId,
      comments: row.comments ?? "",
      isActual: false,
    }));

  const actualRows = Object.entries(state.byProductGeneral)
    .filter(
      ([, sel]) =>
        sel?.deviationNoteId != null && String(sel.deviationNoteId).trim() !== "",
    )
    .map(([productId, sel]) => {
      const pid = Number(productId);
      const erpCode = erpCodeByProductId.get(pid) ?? String(productId);
      return {
        simulationId,
        deviationNoteId: String(sel.deviationNoteId),
        productId: String(pid),
        erpCode,
        periodKey: "",
        periodIndex: 0,
        comments: "",
        isActual: true,
      };
    });

  return [...periodRows, ...actualRows];
}
